import { Controller } from 'stimulus'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const SAVE_LABELS = { returned: 'Record return', discarded: 'Record discard', other: 'Record' }

// A Record Disposition or Edit dialog on a job's Samples tab. The element is
// the dialog's overlay; sample-disposition-card opens it by id.
//
// Choosing an outcome shows its fields ([data-show="store" | "done" |
// "other" | "none"]), disables the hidden ones so they neither block submit
// nor send stale values, and makes [data-required-when] inputs required.
// Storing has one row per box (Which samples, Storage location); a new row
// copies the location above. The label preview follows the dates and the box
// row being edited. Discard after follows Stored on (plus the account's
// retention days) until someone edits it by hand.
export default class extends Controller {
  static targets = ['choice', 'rows', 'row', 'rowTemplate', 'storedOn', 'discardAfter', 'outcomeOn', 'outcomeBy',
    'submit', 'print', 'previewDate', 'previewStored', 'previewContents', 'previewBox', 'previewStatus',
    'previewOutcome']

  connect() {
    this.discardEdited = false
    this.currentRow = 0
    if (this.hasRowTemplateTarget && this.rowTargets.length === 0) this.addBox()
    this.choose()
    if (this.data.get('openOnLoad') === 'true') this.open()
  }

  open() {
    this.element.classList.remove('hidden')
  }

  close(event) {
    if (event) event.preventDefault()
    this.element.classList.add('hidden')
  }

  closeOnEscape(event) {
    if (event.key === 'Escape') this.close()
  }

  choose() {
    const status = this.status()
    const visible = {
      store: status === 'in_storage',
      done: status !== null && status !== 'in_storage',
      other: status === 'other',
      none: status === null
    }

    this.choiceTargets.forEach((choice) => {
      const radio = choice.querySelector('input[type="radio"]')
      choice.classList.toggle('sd-seg__option--on', !!(radio && radio.checked))
    })

    this.element.querySelectorAll('[data-show]').forEach((group) => {
      const show = visible[group.dataset.show]
      group.classList.toggle('hidden', !show)
      group.querySelectorAll('input, select, textarea').forEach(input => input.toggleAttribute('disabled', !show))
    })

    this.element.querySelectorAll('[data-required-when]').forEach((input) => {
      input.toggleAttribute('required', !!visible[input.dataset.requiredWhen])
    })

    this.refresh()
  }

  // ---- box rows ----------------------------------------------------------

  addBox(event) {
    if (event) event.preventDefault()
    const rows = this.rowTargets
    const location = rows.length ? rows[rows.length - 1].querySelector('[data-field="location"]').value : ''
    this.rowsTarget.insertAdjacentHTML('beforeend', this.rowTemplateTarget.innerHTML)
    const row = this.rowTargets[this.rowTargets.length - 1]
    row.querySelector('[data-field="location"]').value = location
    this.currentRow = this.rowTargets.length - 1
    this.renumber()
    if (event) row.querySelector('[data-field="contents"]').focus()
  }

  removeBox(event) {
    event.preventDefault()
    if (this.rowTargets.length < 2) return
    event.currentTarget.closest('[data-target~="sample-disposition-dialog.row"]').remove()
    this.currentRow = Math.min(this.currentRow, this.rowTargets.length - 1)
    this.renumber()
  }

  focusRow(event) {
    const row = event.currentTarget.closest('[data-target~="sample-disposition-dialog.row"]')
    this.currentRow = Math.max(this.rowTargets.indexOf(row), 0)
    this.refresh()
  }

  renumber() {
    const only = this.rowTargets.length < 2
    this.rowTargets.forEach((row, index) => {
      const number = row.querySelector('[data-field="number"]')
      const remove = row.querySelector('[data-field="remove"]')
      number.textContent = `Box ${index + 1}`
      remove.toggleAttribute('disabled', only)
    })
    this.refresh()
  }

  // ---- dates -------------------------------------------------------------

  storedOnChanged() {
    if (this.discardEdited || !this.hasDiscardAfterTarget || !this.storedOnTarget.value) return

    const days = parseInt(this.data.get('retentionDays'), 10) || 90
    const date = new Date(`${this.storedOnTarget.value}T00:00:00`)
    date.setDate(date.getDate() + days)
    const pad = n => String(n).padStart(2, '0')
    this.discardAfterTarget.value = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    this.refresh()
  }

  discardAfterChanged() {
    this.discardEdited = true
    this.refresh()
  }

  // ---- preview and save button -------------------------------------------

  refresh() {
    const status = this.status()
    const rows = this.rowTargets
    const row = rows[this.currentRow]

    if (this.hasPreviewDateTarget && this.hasDiscardAfterTarget) {
      this.previewDateTarget.textContent = (this.format(this.discardAfterTarget.value) || 'No date').toUpperCase()
    }
    if (this.hasPreviewStoredTarget && this.hasStoredOnTarget) {
      const location = row ? row.querySelector('[data-field="location"]').value.trim() : this.fieldValue('location')
      const stored = this.format(this.storedOnTarget.value)
      this.previewStoredTarget.textContent = [stored ? `Stored ${stored}` : '', location ? `in ${location}` : ''].join(' ').trim()
    }
    if (this.hasPreviewContentsTarget) {
      const contents = row ? row.querySelector('[data-field="contents"]').value.trim() : this.fieldValue('contents')
      this.previewContentsTarget.textContent = contents ? `Contents: ${contents}` : ''
    }
    if (this.hasPreviewBoxTarget && rows.length) {
      const existing = parseInt(this.data.get('existingBoxes'), 10) || 0
      this.previewBoxTarget.textContent = `BOX ${existing + this.currentRow + 1} OF ${existing + rows.length}`
    }
    if (this.hasPreviewStatusTarget && status && status !== 'in_storage') {
      const labels = { returned: 'Returned', discarded: 'Discarded', other: 'Other' }
      this.previewStatusTarget.textContent = labels[status]
      if (this.hasPreviewOutcomeTarget) {
        const on = this.hasOutcomeOnTarget ? this.format(this.outcomeOnTarget.value) : ''
        const option = this.hasOutcomeByTarget ? this.outcomeByTarget.options[this.outcomeByTarget.selectedIndex] : null
        const by = option ? option.text : ''
        this.previewOutcomeTarget.textContent = [on, by ? `by ${by}` : ''].join(' ').trim()
      }
    }

    if (this.hasSubmitTarget && this.data.get('mode') !== 'edit') {
      this.submitTarget.value = this.saveLabel(status, rows.length)
    }
  }

  saveLabel(status, count) {
    if (status === 'in_storage') {
      const boxes = `Store ${count} ${count === 1 ? 'box' : 'boxes'}`
      return this.hasPrintTarget && this.printTarget.checked ? `${boxes} and print labels` : boxes
    }
    return SAVE_LABELS[status] || 'Save'
  }

  status() {
    const checked = this.element.querySelector('input[name="sample_disposition[status]"]:checked')
    return checked ? checked.value : null
  }

  fieldValue(field) {
    const input = this.element.querySelector(`[data-field="${field}"]:not([disabled])`)
    return input ? input.value.trim() : ''
  }

  format(iso) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '')
    if (!match) return ''
    return `${MONTHS[parseInt(match[2], 10) - 1]} ${parseInt(match[3], 10)}, ${match[1]}`
  }
}
